import {
  UseFormRegister,
  RegisterOptions,
  UseFormClearErrors,
  UseFormSetValue,
} from "react-hook-form";
import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { IconChevronDown, IconSearch, IconX } from "@tabler/icons-react";
import { FaSpinner } from "react-icons/fa";
import FormErrorMessage from "./common/FormErrorMessage";
import { useLanguage } from "../context/LanguageContext";

type SelectOption = {
  id?: string | number;
  value: string | number;
  text: string;
  img?: string;
};

type Props = {
  name: string;
  label?: string;
  placeholder?: string;
  selectOptions: SelectOption[];
  register?: UseFormRegister<any>;
  validation?: RegisterOptions;
  error?: any;
  className?: string;
  labelClass?: string;
  disabled?: boolean;
  removeDiv?: boolean;
  value?: string | any;
  setValue?: (val: string | any) => void;
  onChange?: (val: string | any) => void;
  clearErrors?: UseFormClearErrors<any>;
  formSetValue?: UseFormSetValue<any>;
  onSearch?: (query: string) => void;
  isPending?: boolean;
  allowCustom?: boolean;
  displayValue?: string;
  compact?: boolean;
  roundedFull?: boolean;
  fullWidthDropdown?: boolean;
  forceDownward?: boolean;
  usePortal?: boolean;
};

export default function SelectInput({
  name,
  label,
  placeholder = "Select",
  selectOptions = [],
  register,
  validation,
  error,
  className = "",
  labelClass = "",
  disabled = false,
  removeDiv = false,
  value,
  setValue,
  onChange,
  clearErrors,
  formSetValue,
  onSearch,
  isPending,
  allowCustom = false,
  displayValue,
  compact = false,
  roundedFull = false,
  fullWidthDropdown = false,
  forceDownward = false,
  usePortal = true,
}: Props) {
  const { t } = useLanguage();
  const [selectOpen, setSelectOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [openUpward, setOpenUpward] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
  } | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const filteredOptions = searchText
    ? selectOptions?.filter((opt) =>
        opt?.text?.toLowerCase()?.includes(searchText?.toLowerCase())
      )
    : selectOptions;

  const hasValue = value != null && value !== "";

  const selectedText = displayValue ?? (hasValue
    ? selectOptions?.find(
        (opt) =>
          (opt?.value != null && String(opt?.value).toLowerCase() === String(value).toLowerCase()) ||
          (opt?.id != null && String(opt?.id).toLowerCase() === String(value).toLowerCase()) ||
          (opt?.text != null && String(opt?.text).toLowerCase() === String(value).toLowerCase())
      )?.text || (allowCustom && typeof value === "string" ? value : "")
    : "");

  // Clear selection
  const handleClear = (
    e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLDivElement>
  ) => {
    e.stopPropagation();
    if (setValue) setValue("");
    if (onChange) onChange("");
    if (clearErrors) clearErrors(name);
    if (formSetValue) formSetValue(name, "");
    setSearchText("");
    setSelectOpen(false);
  };

  const handleSelect = (val: string | number) => {
    if (setValue) setValue(val);
    if (onChange) {
      try {
        onChange(val);
      } catch (err) {
        onChange({ target: { name, value: val } } as any);
      }
    }
    if (clearErrors) clearErrors(name);
    if (formSetValue) formSetValue(name, val);
    setSelectOpen(false);
    setSearchText("");
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchText(val);
    if (onSearch) onSearch(val);
  };

  // Compute dropdown position when opened
  const updateDropdownPosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const shouldOpenUpward =
      !forceDownward && spaceBelow < 250 && spaceAbove > 250;
    setOpenUpward(shouldOpenUpward);

    if (shouldOpenUpward) {
      setDropdownPosition({
        bottom: window.innerHeight - rect.top + 6,
        left: rect.left,
        width: rect.width,
      });
    } else {
      setDropdownPosition({
        top: rect.bottom + 6,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [forceDownward]);

  // Toggle dropdown
  const handleDropdownToggle = () => {
    if (disabled) return;
    if (!selectOpen) {
      updateDropdownPosition();
    }
    setSelectOpen((prev) => !prev);
    if (!selectOpen) setTimeout(() => inputRef.current?.focus(), 0);
  };

  // Update position on scroll/resize while open
  useEffect(() => {
    if (!selectOpen) return;
    const handleUpdate = () => updateDropdownPosition();
    window.addEventListener("scroll", handleUpdate, true);
    window.addEventListener("resize", handleUpdate);
    return () => {
      window.removeEventListener("scroll", handleUpdate, true);
      window.removeEventListener("resize", handleUpdate);
    };
  }, [selectOpen, updateDropdownPosition]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setSelectOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keyboard support
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectOpen) return;
      if (e.key === "Escape") {
        setSelectOpen(false);
        setSearchText("");
      }
      if (e.key === "Backspace" && searchText === "" && hasValue) {
        const fakeEvent = {
          stopPropagation: () => {},
          currentTarget: containerRef.current!,
        } as React.KeyboardEvent<HTMLDivElement>;
        handleClear(fakeEvent);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [selectOpen, searchText, hasValue]);

  // Dropdown content
  const dropdownContent = (
    <div
      className={`rounded-lg bg-yellow-50 border border-[#D8D8D8]/20 max-h-80 overflow-y-auto min-w-full ${
        fullWidthDropdown ? "w-full" : "w-max max-w-[280px]"
      }`}
    >
      <div className="p-2">
        <div className="flex items-center gap-2 px-2 py-1 bg-gray-100 rounded-md">
          <IconSearch size={16} className="text-gray-500" />
          <input
            ref={inputRef}
            type="search"
            className="w-full bg-transparent text-xs outline-none placeholder:text-gray-400"
            placeholder={t("Search...")}
            value={searchText}
            onChange={handleSearchChange}
            onClick={(e) => e.stopPropagation()}
          />
          {isPending && <FaSpinner className="animate-spin text-gray-500 ml-1 shrink-0" size={14} />}
        </div>
      </div>
      <ul>
        {filteredOptions?.map((opt, i) => (
          <li
            key={opt.id ?? i}
            className={`px-4 py-2 text-xs hover:bg-blue-100 cursor-pointer ${
              String(opt.value) === String(value) ? "bg-blue-50 font-medium" : ""
            }`}
            onClick={() => handleSelect(opt.value)}
          >
            {t(opt.text)}
          </li>
        ))}
        {searchText && filteredOptions?.length === 0 && !allowCustom && (
          <li className="px-4 py-2 text-xs text-gray-400">
            {t("No results found")}
          </li>
        )}
        {searchText && allowCustom && !filteredOptions?.some(o => o.text.toLowerCase() === searchText.toLowerCase()) && (
          <li 
            className="px-4 py-2 text-xs hover:bg-blue-100 cursor-pointer flex items-center gap-2 text-blue-600 font-medium"
            onClick={() => handleSelect(searchText)}
          >
            Use "{searchText}"
          </li>
        )}
      </ul>
    </div>
  );

  const labelText = label || placeholder;
  const isFloating = !compact && (selectOpen || hasValue || Boolean(selectedText));

  const borderClass = error
    ? "border-red-500"
    : disabled
    ? "border-slate-200"
    : "border-[#FBD037]";

  const focusRing = error
    ? "ring-2 ring-red-500/20"
    : selectOpen
    ? "ring-2 ring-[#FBD037]/30"
    : "";

  const selectField = (
    <div ref={containerRef} className="relative w-full">
      <div
        className={`flex items-center justify-between min-w-[100px] ${
          compact ? "h-8 px-3 text-xs" : "px-4 pt-3.5 pb-2.5 text-xs"
        } ${roundedFull ? "rounded-full" : "rounded-lg"} border ${borderClass} ${focusRing} ${
          disabled
            ? "bg-gray-100 text-gray-500 cursor-not-allowed"
            : "bg-yellow-50 cursor-pointer text-[#1E293B]"
        } transition-all duration-200`}
        onClick={handleDropdownToggle}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleDropdownToggle();
          }
        }}
      >
        <span className={`truncate flex-1 ${disabled ? "text-gray-500 font-normal" : "text-[#1E293B]"}`}>
          {selectedText ? t(selectedText) : compact ? t(placeholder) : ""}
        </span>

        <div className="flex items-center gap-1 z-10">
          {hasValue && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="p-0.5 rounded-full hover:bg-gray-200 transition-colors"
              aria-label="Clear selection"
            >
              <IconX size={16} className="text-gray-500" />
            </button>
          )}
          <IconChevronDown
            size={18}
            stroke={2.2}
            className={`${disabled ? "text-gray-400" : "text-gray-500"} transition-transform ${
              selectOpen ? "rotate-180" : ""
            }`}
          />
        </div>

        {/* Animated Floating Label */}
        {!compact && labelText && (
          <label
            className={`absolute left-2.5 pointer-events-none transition-all duration-200 ease-in-out select-none rounded-md
              ${
                isFloating
                  ? `-top-2.5 text-[11px] font-semibold ${disabled ? "!bg-gray-100 !text-gray-400" : "bg-yellow-50 !text-black font-medium"} z-10 px-1.5`
                  : "top-1/2 -translate-y-1/2 text-xs bg-transparent z-0 px-1"
              }
              ${error ? "!text-red-500" : disabled ? "!text-gray-400" : ""}
              ${labelClass}
            `}
          >
            {t(labelText)}
            {validation?.required && (
              <span className="text-red-500 ml-0.5">*</span>
            )}
          </label>
        )}
      </div>

      {/* Render dropdown inline or via portal */}
      {selectOpen &&
        (usePortal && dropdownPosition ? (
          createPortal(
            <div
              ref={dropdownRef}
              style={{
                position: "fixed",
                top: dropdownPosition.top,
                bottom: dropdownPosition.bottom,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
                zIndex: 9999,
              }}
              className=""
            >
              {dropdownContent}
            </div>,
            document.body
          )
        ) : (
          <div ref={dropdownRef} className="absolute z-50">
            {dropdownContent}
          </div>
        ))}

      {/* Hidden input for RHF */}
      {register && !formSetValue && (
        <input
          type="hidden"
          {...register(name, validation)}
          value={value ?? ""}
        />
      )}
    </div>
  );

  return (
    <div className={compact ? `relative ${className}` : `relative pt-1.5 ${className}`}>
      <div className="w-full flex-1 min-w-0">
        {removeDiv ? (
          selectField
        ) : (
          <div className="relative">{selectField}</div>
        )}
        <FormErrorMessage error={error} />
      </div>
    </div>
  );
}
